/**
 * Portal Material Access Policy API Client
 *
 * Two things live behind this endpoint:
 *  - the read-only audit of who opened which course group material, how often,
 *    how many views/prints they have left, and the raw click log that counts
 *    every single open (portal_admin and above);
 *  - the per-teacher "digital materials off" switch (director / manager only).
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to the material-access-policy endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/material-access-policy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ operation, data })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * List the teachers the current user may see, with their digital material
 * state and view statistics. The statistics start from each campus's last
 * stats reset unless includeHistory is set.
 */
export const listTeacherPolicies = async ({ includeHistory = false } = {}) => {
  const result = await apiRequest('listTeachers', { includeHistory });
  return {
    teachers: result.teachers || [],
    statsResets: result.statsResets || [],
    campusRestricted: result.campusRestricted,
    canManagePolicy: result.canManagePolicy,
    requesterCampusIds: result.requesterCampusIds || []
  };
};

/**
 * Turn digital materials on or off for one teacher.
 * Director / manager only - the backend rejects anyone else.
 */
export const setTeacherPolicy = async (userId, blocked, reason = '') => {
  const result = await apiRequest('setPolicy', { userId, blocked, reason });
  return result.policy;
};

/**
 * Read the material access audit.
 *
 * @param {Object} filters - { userId, campusId, startDate, endDate, limit, includeHistory }
 */
export const listMaterialAccessLogs = async (filters = {}) => {
  const result = await apiRequest('listAccessLogs', filters);
  return {
    records: result.records || [],
    clicks: result.clicks || [],
    summary: result.summary || [],
    usage: result.usage || [],
    totalRecords: result.totalRecords || 0,
    totalClicks: result.totalClicks || 0,
    truncated: result.truncated || false,
    clicksTruncated: result.clicksTruncated || false,
    campusRestricted: result.campusRestricted,
    canManagePolicy: result.canManagePolicy
  };
};

/**
 * Start the displayed statistics of the given campuses from now (term start).
 * Display only - no record is deleted and no quota changes. Director / manager only.
 */
export const resetMaterialStats = async (campusIds) => {
  const result = await apiRequest('resetStats', { campusIds });
  return result.resetAt;
};

/**
 * For one teacher: every material assigned to a course group they teach
 * that matches their subject there, with view/print usage, remaining quota
 * (grants included), and the individual access timeline.
 */
export const getTeacherMaterialDetail = async (userId) => {
  const result = await apiRequest('getTeacherMaterialDetail', { userId });
  return {
    teacher: result.teacher || null,
    materials: result.materials || [],
    canManagePolicy: result.canManagePolicy
  };
};

/**
 * Grant a teacher extra views or prints on one material within one course
 * group, on top of the default 3-use limit. Director / manager only.
 */
export const grantExtraAccess = async (userId, materialId, courseGroupId, operationType, amount, reason = '') => {
  const result = await apiRequest('grantExtraAccess', {
    userId, materialId, courseGroupId, operationType, amount, reason
  });
  return result.grant;
};

const materialAccessPolicyApi = {
  listTeacherPolicies,
  setTeacherPolicy,
  listMaterialAccessLogs,
  getTeacherMaterialDetail,
  grantExtraAccess,
  resetMaterialStats
};

export default materialAccessPolicyApi;
